import { useToast } from "@/components/ui/use-toast";
import { useCreateApplicationMutation, useGetUserDataQuery } from "@/src/api/Inhabitant";
import { useGetProcedureQuery } from "@/src/api/Tenant";
import Container from "@/src/components/Container/Container";
import ProcedureDownloads from "@/src/components/ProcedureDownloads/ProcedureDownloads";
import AppForm from "@/src/components/Form";
import { CreateApplicationFormValuesType } from "@/src/components/Form/schemas";
import Loader from "@/src/components/Loader/Loader";
import useAuth from "@/src/hooks/useAuth";
import { createApplicationRoute } from "@/src/routes/router";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

/**
 * Czy access token jest świeży (do `bufferSec` przed wygaśnięciem).
 * Bez tego `useGetUserDataQuery` na publicznej stronie strzela
 * privateApiClient z wygasłym tokenem → 401 → refresh → logout →
 * redirect na /$tenant/login. Czyli niezalogowany user, który ma
 * w sessionStorage stare auth (np. po wcześniejszym logowaniu jako
 * admin), zostaje wyrzucony na login mimo że strona jest publiczna.
 */
function isAccessTokenValid(accessToken: string | undefined, bufferSec = 5): boolean {
  if (!accessToken) return false;
  try {
    const payload = jwtDecode<{ exp?: number }>(accessToken);
    if (!payload?.exp) return false;
    const expiresInSec = payload.exp - Math.floor(Date.now() / 1000);
    return expiresInSec > bufferSec;
  } catch {
    return false;
  }
}

function CreateApplicationPage() {
  const { tenant, formId } = createApplicationRoute.useParams();
  const draftStorageKey = `draft.${tenant}.${formId}`;
  const navigate = useNavigate();
  const {
    auth: { userId, isLoggedIn, accessToken },
  } = useAuth();
  // Sprawdzamy świeżość tokena z buforem 5s. Jeśli token wygasł, traktujemy
  // jak niezalogowanego — formularz dostępny dla gościa, request po userData
  // nie wystrzeli i nie wywoła łańcucha 401 → refresh → logout → redirect
  // na login.
  const hasFreshToken = isAccessTokenValid(accessToken, 5);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState<{ id: string; refNumber: string; confirmationKey: string } | null>(null);

  const { toast } = useToast();
  const {
    data: procedureData,
    isError: isGetProcedureError,
    isLoading: isProcedureLoading,
  } = useGetProcedureQuery(tenant, formId);
  const isComment = String(procedureData?.[0]?.payload?.typeOfLetter || "").includes("2.2");
  const letterNom = isComment ? "Uwaga" : "Wniosek";
  const letterGen = isComment ? "uwagi" : "wniosku";
  const letterAcc = isComment ? "uwagę" : "wniosek";

  const { mutate: createApplication } = useCreateApplicationMutation({
    onSuccess: (data) => {
      setSubmitted(true);
      setSubmittedData({ id: data.id, refNumber: data.refNumber, confirmationKey: data.confirmationKey });
      try {
        localStorage.removeItem(draftStorageKey);
      } catch {
        /* ignore */
      }
      toast({
        variant: "success",
        title: `${letterNom} ${isComment ? "zapisana" : "zapisany"}`,
        description: `Sprawdź swoją skrzynkę email i potwierdź ${letterAcc} klikając link w wiadomości.`,
      });
    },
    onError: (e) => {
      const apiMessage = (e.response?.data as { message?: string | string[] } | undefined)?.message;
      const detail = Array.isArray(apiMessage) ? apiMessage.join(", ") : apiMessage;
      toast({
        variant: "destructive",
        title: "Błąd " + (e.response?.status ?? e.code ?? ""),
        description: detail || `Podczas wysyłania ${letterGen} wystąpił błąd.`,
      });
    },
  });

  // Tylko zalogowani z aktualnym tokenem → fetchuj dane usera. Wygasły
  // token oznacza efektywnie "niezalogowany" — bez tego sprawdzenia
  // request leci z expired tokenem, backend zwraca 401, interceptor
  // wywołuje logout (redirect na login).
  const {
    data: userData,
    isLoading: isUserLoading,
  } = useGetUserDataQuery(
    { tenantId: tenant, userId },
    isLoggedIn && !!userId && hasFreshToken,
  );

  if (isProcedureLoading || (isLoggedIn && hasFreshToken && isUserLoading)) {
    return <Loader />;
  }

  const [currentProcedure] = procedureData || [];
  const { payload: procedurePayload, displayName, description, files } = currentProcedure || {};

  const renderProcedureData = () => {
    if (isGetProcedureError || !procedureData?.length) {
      return <p>Brak danych procedury</p>;
    }
    return (
      <div>
        <h1 className="text-2xl mb-4">{displayName}</h1>
        <p className="my-1 text-base">{description}</p>
        <p className="my-1 text-base">{procedurePayload?.applicationPlace}</p>
        <p className="my-1 text-base">{procedurePayload?.spatialPlanningActType}</p>
        <p className="my-1 text-base">{procedurePayload?.typeOfLetter}</p>
        {currentProcedure && (
          <div className="my-3">
            <ProcedureDownloads
              tenant={tenant}
              procedureId={currentProcedure.id}
              files={files}
              payload={procedurePayload}
              collapsible
              title="Dokumenty projektu planu"
            />
          </div>
        )}
      </div>
    );
  };

  /** Czyści zapisany szkic w localStorage i wraca na publiczną stronę
   *  procedury. Bez confirm — user kliknął świadomie, nie chcemy go irytować
   *  pytaniami; zachowanie analogiczne do "kosza" w innych formularzach. */
  const handleCancel = () => {
    if (!confirm("Na pewno zrezygnować z wypełniania? Wprowadzone dane zostaną usunięte.")) return;
    try {
      localStorage.removeItem(draftStorageKey);
      sessionStorage.removeItem("selectedParcels");
      sessionStorage.removeItem("applicationDraftId");
    } catch {
      /* ignore */
    }
    // Wróć na stronę procedury (jeśli mamy slug) lub na stronę gminy.
    const slug = currentProcedure?.name;
    if (slug) {
      navigate({ to: "/$tenant/$procedureSlug", params: { tenant, procedureSlug: slug } });
    } else {
      navigate({ to: "/$tenant", params: { tenant } });
    }
  };

  if (submitted && submittedData) {
    const pdfUrl = `${import.meta.env.VITE_API_URL}/tenant/${tenant}/application/${submittedData.id}/download/${submittedData.confirmationKey}`;
    return (
      <Container narrow>
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center mt-8">
          <div className="text-green-500 text-5xl mb-4">&#9993;</div>
          <h2 className="text-2xl font-bold text-green-700 mb-3">{letterNom} {isComment ? "została zapisana" : "został zapisany"}</h2>
          <p className="text-gray-600 mb-1">Numer referencyjny:</p>
          <p className="text-2xl font-mono font-bold text-blue-700 mb-4">{submittedData.refNumber}</p>
          <p className="text-gray-700 mb-2">Na podany adres email wysłaliśmy link potwierdzający.</p>
          <p className="text-gray-700 font-semibold mb-4">Kliknij link w wiadomości aby potwierdzić {letterAcc}.</p>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors mb-4"
          >
            Pobierz {letterAcc} jako PDF
          </a>
          <p className="text-xs text-gray-500">Zachowaj numer referencyjny — służy do identyfikacji Twojego pisma.</p>
          <p className="text-sm text-red-600 mt-4">Brak potwierdzenia w ciągu 24 godzin spowoduje anulowanie {letterGen}.</p>
        </div>
      </Container>
    );
  }

  const onSubmit = (value: CreateApplicationFormValuesType) => {
    // Pola pisma wynikające z procedury (pkt 1–3, 7.2.1, 7.3.1). Backend i tak
    // nadpisuje je z rekordu procedury — tu wysyłamy tylko te, bez całego
    // payloadu procedury (ogłoszenie HTML, warstwy mapy, geoankieta).
    const letterFields = {
      applicationPlace: procedurePayload?.applicationPlace,
      typeOfLetter: procedurePayload?.typeOfLetter,
      spatialPlanningActType: procedurePayload?.spatialPlanningActType,
      spatialPlanningActName: procedurePayload?.spatialPlanningActName,
      municipalitysGeneralPlanOrLocalDevelopmentPlan: procedurePayload?.municipalitysGeneralPlanOrLocalDevelopmentPlan,
    };
    createApplication({ tenantId: tenant, formId, ...value, ...letterFields });
  };

  // Load pre-selected parcels from map (sessionStorage) and merge into draft
  const preSelectedParcels = (() => {
    try {
      const stored = sessionStorage.getItem("selectedParcels");
      if (stored) {
        sessionStorage.removeItem("selectedParcels");
        const parcels = JSON.parse(stored);
        // Merge pre-selected parcels into the draft so they persist
        if (parcels && parcels.length > 0) {
          try {
            const draftRaw = localStorage.getItem(draftStorageKey);
            const draft = draftRaw ? JSON.parse(draftRaw) : {};
            if (!draft.parcels || draft.parcels.length === 0) {
              draft.parcels = parcels.map((p: any) => ({
                "7point2": { "1": "", "2": p.id },
                "7point3": {},
                geometry: p.geometry,
              }));
              localStorage.setItem(draftStorageKey, JSON.stringify(draft));
            }
          } catch {
            /* ignore localStorage errors */
          }
        }
        return parcels;
      }
    } catch { /* */ }
    return undefined;
  })();

  const isAuthenticatedInhabitant = isLoggedIn && hasFreshToken;
  const defaultValues: Partial<CreateApplicationFormValuesType> | undefined =
    isAuthenticatedInhabitant && userData?.payload
      ? { applicants: { ...userData.payload, parcels: undefined } }
      : undefined;

  return (
    <Container narrow>
      {!isAuthenticatedInhabitant && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-800">
            Składasz wniosek jako <strong>gość</strong>. Po wypełnieniu formularza otrzymasz email z linkiem potwierdzającym.
          </p>
        </div>
      )}
      {renderProcedureData()}
      <div className="flex justify-end mt-2 mb-2">
        <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
          <X className="h-3.5 w-3.5 mr-1" />
          Zrezygnuj z wypełniania
        </Button>
      </div>
      <AppForm.CreateApplication
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        applicantsParcels={userData?.payload?.parcels}
        procedurePayload={procedurePayload}
        preSelectedParcels={preSelectedParcels}
        draftStorageKey={draftStorageKey}
        onSubmitSuccess={() => {
          try {
            localStorage.removeItem(draftStorageKey);
          } catch {
            /* ignore */
          }
        }}
      />
    </Container>
  );
}

export default CreateApplicationPage;
